import React, { createContext, useState, useEffect, useContext } from 'react';
import axios from 'axios';

// Define the user type
interface User {
  id: string;
  name: string;
  email: string;
}

// Define the auth context type
interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (name: string, email: string, password: string) => Promise<void>;
  logout: () => void;
}

// API base URL from environment variables with fallback
const BASE_URL = import.meta.env.VITE_BASE_URL || 'https://tacoai.teamta.net';

// Create the auth context
const AuthContext = createContext<AuthContextType | null>(null);

// Create a provider component
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  // Check if user is already logged in on mount
  useEffect(() => {
    const checkAuthStatus = async () => {
      const token = localStorage.getItem('token');
      
      if (token) {
        try {
          // Configure axios headers
          axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
          
          // Fetch current user data
          const response = await axios.get(`${BASE_URL}/me`);
          setUser(response.data);
        } catch (error) {
          console.error('Authentication check failed', error);
          localStorage.removeItem('token');
          delete axios.defaults.headers.common['Authorization'];
        }
      }
      
      setLoading(false);
    };
    
    checkAuthStatus();
  }, []);
  
  // Login function
  const login = async (email: string, password: string) => {
    try {
      const response = await axios.post(`${BASE_URL}/auth/login`, { email, password });
      const { access_token } = response.data;
      
      // Save token to local storage
      localStorage.setItem('token', access_token);
      
      // Set authorization header
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      // Fetch user data
      const userResponse = await axios.get(`${BASE_URL}/me`);
      setUser(userResponse.data);
    } catch (error) {
      console.error('Login failed', error);
      throw new Error('Login failed. Please check your credentials.');
    }
  };
  
  // Register function
  const register = async (name: string, email: string, password: string) => {
    try {
      await axios.post(`${BASE_URL}/auth/register`, { name, email, password });
      // After successful registration, log the user in
      await login(email, password);
    } catch (error) {
      console.error('Registration failed', error);
      throw new Error('Registration failed. Please try again.');
    }
  };
  
  // Logout function
  const logout = () => {
    // Remove token from storage
    localStorage.removeItem('token');
    
    // Remove auth header
    delete axios.defaults.headers.common['Authorization'];
    
    // Reset user state
    setUser(null);
  };
  
  return (
    <AuthContext.Provider value={{ 
      user, 
      isAuthenticated: !!user,
      loading,
      login,
      register,
      logout
    }}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook for using auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  
  return context;
};